import { useState, useMemo, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import {
  Cpu, FileText, Building2, Tag, Hash, Zap,
  Search, BookMarked, ArrowRight, ChevronLeft, ChevronRight,
  LayoutGrid, List,
} from "lucide-react";
import type { GlossaryEntry } from "@shared/schema";

const PAGE_SIZE = 50;

const TYPE_CONFIG: Record<string, { label: string; icon: typeof Cpu; color: string; bg: string }> = {
  technical_term:      { label: "Technical Term", icon: Cpu,       color: "text-blue-600 dark:text-blue-400",   bg: "bg-blue-500/10" },
  standard:            { label: "Standard",        icon: FileText,  color: "text-purple-600 dark:text-purple-400", bg: "bg-purple-500/10" },
  product_brand:       { label: "Brand",           icon: Building2, color: "text-green-600 dark:text-green-400",  bg: "bg-green-500/10" },
  product_description: { label: "Product",         icon: Tag,       color: "text-orange-600 dark:text-orange-400", bg: "bg-orange-500/10" },
  acronym:             { label: "Acronym",          icon: Hash,      color: "text-indigo-600 dark:text-indigo-400", bg: "bg-indigo-500/10" },
  concept:             { label: "Concept",          icon: Zap,       color: "text-pink-600 dark:text-pink-400",   bg: "bg-pink-500/10" },
};

const TYPE_FILTERS = [
  { value: "all",              label: "All" },
  { value: "product_brand",   label: "Brands" },
  { value: "acronym",         label: "Acronyms" },
  { value: "technical_term",  label: "Technical Terms" },
  { value: "standard",        label: "Standards" },
  { value: "product_description", label: "Products" },
  { value: "concept",         label: "Concepts" },
] as const;

type TypeFilter = typeof TYPE_FILTERS[number]["value"];
type ViewMode = "grid" | "table";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

function TypeBadge({ type }: { type: string | null | undefined }) {
  const cfg = TYPE_CONFIG[type ?? ""] ?? TYPE_CONFIG.technical_term;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
      <Icon className="h-2.5 w-2.5" />
      {cfg.label}
    </span>
  );
}

function EntryCard({ entry }: { entry: GlossaryEntry }) {
  const termSlug = entry.slug || entry.normalizedTerm;
  return (
    <Link
      href={`/glossary/${termSlug}`}
      data-testid={`card-glossary-${entry.id}`}
      className="group flex flex-col gap-2 rounded-lg border bg-card p-4 hover:shadow-md transition-all hover:border-primary/40 cursor-pointer"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-semibold text-sm leading-snug group-hover:text-primary transition-colors">
          {entry.title}
        </h3>
        <TypeBadge type={entry.keywordType} />
      </div>
      {entry.definition && (
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
          {entry.definition}
        </p>
      )}
      {entry.categoryTags && entry.categoryTags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {entry.categoryTags.slice(0, 3).map((tag) => (
            <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0">
              {tag}
            </Badge>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1 text-[11px] font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
        Read more <ArrowRight className="h-2.5 w-2.5" />
      </div>
    </Link>
  );
}

function EntrySkeleton() {
  return (
    <div className="rounded-lg border bg-card p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-20 rounded-full" />
      </div>
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-3/4" />
    </div>
  );
}

export default function GlossaryIndexPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [jumpLetter, setJumpLetter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  const { data: entries, isLoading } = useQuery<GlossaryEntry[]>({
    queryKey: ["/api/glossary"],
  });

  const sorted = useMemo(() => {
    if (!entries) return [];
    return [...entries].sort((a, b) =>
      a.title.localeCompare(b.title, undefined, { sensitivity: "base" })
    );
  }, [entries]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sorted.filter((e) => {
      const matchesSearch = !q || e.title.toLowerCase().includes(q) || (e.definition ?? "").toLowerCase().includes(q);
      const matchesLetter = !jumpLetter || e.title.toUpperCase().startsWith(jumpLetter);
      const matchesType = typeFilter === "all" || e.keywordType === typeFilter;
      return matchesSearch && matchesLetter && matchesType;
    });
  }, [sorted, search, jumpLetter, typeFilter]);

  useEffect(() => { setPage(1); }, [search, jumpLetter, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const activeLetters = useMemo(() => {
    const set = new Set(sorted.map((e) => e.title[0]?.toUpperCase() ?? "#"));
    return set;
  }, [sorted]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const e of sorted) {
      const k = e.keywordType ?? "technical_term";
      counts[k] = (counts[k] ?? 0) + 1;
    }
    return counts;
  }, [sorted]);

  const isSearching = search.trim().length > 0 || !!jumpLetter || typeFilter !== "all";

  return (
    <>
      <Helmet>
        <title>Glossary | Future Manufacturing</title>
        <meta
          name="description"
          content="Browse the Future Manufacturing industry glossary — definitions and explanations of technical terms, standards, acronyms, and concepts across engineering and manufacturing."
        />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-7xl px-4 pt-4 w-full">
          <PageBreadcrumb items={[{ label: "Glossary" }]} />
        </div>

        <main className="flex-1">
          {/* Hero */}
          <section className="border-b bg-muted/30 py-14">
            <div className="mx-auto max-w-7xl px-4">
              <Badge variant="secondary" className="mb-4">Industry Glossary</Badge>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
                Every term, defined
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mb-8">
                Definitions and explanations of the technical terms, standards, acronyms, and concepts used across the whole bill of materials.
              </p>
              {/* Search */}
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search terms…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setJumpLetter(null);
                  }}
                  className="pl-9"
                  data-testid="input-glossary-search"
                />
              </div>
            </div>
          </section>

          {/* Sticky filter bar: A–Z + type filters + view toggle */}
          {!isLoading && sorted.length > 0 && (
            <div className="border-b bg-background sticky top-0 z-10 shadow-sm">
              {/* A–Z strip */}
              <div className="mx-auto max-w-7xl px-4 pt-2 pb-1 flex flex-wrap gap-0.5 items-center">
                <button
                  onClick={() => { setJumpLetter(null); setSearch(""); }}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    !jumpLetter && !search ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`}
                  data-testid="btn-alpha-all"
                >
                  All
                </button>
                {ALPHABET.map((letter) => {
                  const active = activeLetters.has(letter);
                  const selected = jumpLetter === letter;
                  return (
                    <button
                      key={letter}
                      disabled={!active}
                      onClick={() => { setJumpLetter(selected ? null : letter); setSearch(""); }}
                      className={`w-7 h-7 rounded text-xs font-medium transition-colors ${
                        selected
                          ? "bg-primary text-primary-foreground"
                          : active
                          ? "text-foreground hover:bg-muted"
                          : "text-muted-foreground/30 cursor-not-allowed"
                      }`}
                      data-testid={`btn-alpha-${letter}`}
                    >
                      {letter}
                    </button>
                  );
                })}
              </div>

              {/* Type filter chips + view toggle */}
              <div className="mx-auto max-w-7xl px-4 pb-2 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex flex-wrap gap-1.5">
                  {TYPE_FILTERS.map(({ value, label }) => {
                    const count = value === "all" ? sorted.length : (typeCounts[value] ?? 0);
                    if (value !== "all" && count === 0) return null;
                    const selected = typeFilter === value;
                    return (
                      <button
                        key={value}
                        onClick={() => setTypeFilter(value)}
                        data-testid={`btn-type-${value}`}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors border ${
                          selected
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
                        }`}
                      >
                        {value !== "all" && (() => {
                          const cfg = TYPE_CONFIG[value];
                          const Icon = cfg?.icon;
                          return Icon ? <Icon className="h-3 w-3" /> : null;
                        })()}
                        {label}
                        <span className={`rounded-full px-1.5 py-0 text-[10px] font-semibold ${
                          selected ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground"
                        }`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Card / Table toggle */}
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant={viewMode === "grid" ? "default" : "outline"}
                    size="icon"
                    onClick={() => setViewMode("grid")}
                    data-testid="btn-view-grid"
                    className="h-7 w-7"
                  >
                    <LayoutGrid className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant={viewMode === "table" ? "default" : "outline"}
                    size="icon"
                    onClick={() => setViewMode("table")}
                    data-testid="btn-view-table"
                    className="h-7 w-7"
                  >
                    <List className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          <section className="py-10">
            <div className="mx-auto max-w-7xl px-4">
              {isLoading ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Array.from({ length: 12 }).map((_, i) => <EntrySkeleton key={i} />)}
                </div>
              ) : filtered.length === 0 && sorted.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-5">
                    <BookMarked className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h2 className="text-xl font-semibold mb-2">No glossary terms yet</h2>
                  <p className="text-muted-foreground max-w-sm">
                    Glossary entries will appear here as they are published.
                  </p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <Search className="h-10 w-10 text-muted-foreground mb-4" />
                  <h2 className="text-xl font-semibold mb-2">No results found</h2>
                  <p className="text-muted-foreground">
                    Try a different search term or{" "}
                    <button
                      onClick={() => { setSearch(""); setJumpLetter(null); setTypeFilter("all"); }}
                      className="underline hover:text-foreground"
                    >
                      clear all filters
                    </button>
                    .
                  </p>
                </div>
              ) : (
                <>
                  {/* Count + page info */}
                  <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                    <p className="text-sm text-muted-foreground">
                      {filtered.length} term{filtered.length !== 1 ? "s" : ""}
                      {isSearching ? (
                        <button
                          onClick={() => { setSearch(""); setJumpLetter(null); setTypeFilter("all"); }}
                          className="ml-2 underline hover:text-foreground"
                        >
                          clear
                        </button>
                      ) : null}
                    </p>
                    {totalPages > 1 && (
                      <p className="text-sm text-muted-foreground">
                        Page {page} of {totalPages}
                      </p>
                    )}
                  </div>

                  {/* Grid or Table */}
                  {viewMode === "grid" ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {paginated.map((entry) => (
                        <EntryCard key={entry.id} entry={entry} />
                      ))}
                    </div>
                  ) : (
                    <Card className="overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Term</TableHead>
                            <TableHead className="w-36">Type</TableHead>
                            <TableHead className="hidden md:table-cell">Definition</TableHead>
                            <TableHead className="hidden lg:table-cell w-48">Categories</TableHead>
                            <TableHead className="w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginated.map((entry) => {
                            const termSlug = entry.slug || entry.normalizedTerm;
                            return (
                              <TableRow
                                key={entry.id}
                                className="cursor-pointer hover:bg-muted/50"
                                data-testid={`row-glossary-${entry.id}`}
                              >
                                <TableCell>
                                  <Link href={`/glossary/${termSlug}`}>
                                    <span className="font-medium text-sm hover:underline hover:text-primary">
                                      {entry.title}
                                    </span>
                                  </Link>
                                </TableCell>
                                <TableCell>
                                  <TypeBadge type={entry.keywordType} />
                                </TableCell>
                                <TableCell className="hidden md:table-cell text-xs text-muted-foreground max-w-sm">
                                  <span className="line-clamp-2">{entry.definition ?? "—"}</span>
                                </TableCell>
                                <TableCell className="hidden lg:table-cell">
                                  {entry.categoryTags && entry.categoryTags.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {entry.categoryTags.slice(0, 2).map((tag) => (
                                        <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0">
                                          {tag}
                                        </Badge>
                                      ))}
                                      {entry.categoryTags.length > 2 && (
                                        <span className="text-[10px] text-muted-foreground">+{entry.categoryTags.length - 2}</span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">—</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Link href={`/glossary/${termSlug}`}>
                                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                                  </Link>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Card>
                  )}

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-3 mt-10">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        data-testid="btn-glossary-prev"
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        {page} / {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        data-testid="btn-glossary-next"
                      >
                        Next <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
